import { useState, useEffect, useRef } from 'react';
import { Loader2, Zap, ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { getScoreColor, getScoreCategory } from '@/lib/utils';
import ChatMarkdown from './ChatMarkdown';
import ChartRenderer from './ChartRenderer';
import { SqlToggle } from './SqlToggle';
import { VisualizationEmbed } from './VisualizationEmbed';
import type { Message } from '@/types/dashboard';

export interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  activityStatus: string;
  elapsedTime: number;
  onSuggestionClick?: (text: string) => void;
}

export default function MessageList({
  messages,
  isLoading,
  activityStatus,
  elapsedTime,
  onSuggestionClick,
}: MessageListProps) {
  const [expandedEval, setExpandedEval] = useState<Record<string, boolean>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottom = useRef(true);

  const toggleEvalExpand = (messageId: string) => {
    setExpandedEval(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  };

  useEffect(() => {
    if (isNearBottom.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div ref={messagesContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 lg:p-6">
      {messages.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-2xl">
            <img
              src="/assets/logo.png"
              alt="Roamler"
              className="w-24 h-24 mx-auto mb-6 rounded-2xl object-contain drop-shadow-2xl"
            />
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Roamler Data Assistant
            </h2>
            <p className="text-muted-foreground mb-6">
              Search 248K outlets, match customer files, or scope markets with natural language
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              {[
                { text: 'Which outlets sell Heineken in Paris?', label: 'Search' },
                { text: 'Find bars that carry Fritz Kola but not Coca-Cola', label: 'ICP / Brand' },
                { text: 'Show me restaurants with a terrace in Lyon', label: 'Search' },
                { text: 'Match these outlets against the Roamler database', label: 'Matchfile' },
              ].map(({ text: suggestion, label }) => (
                <button
                  key={suggestion}
                  onClick={() => onSuggestionClick?.(suggestion)}
                  className="bg-card/50 border border-primary/20 rounded-lg p-4 hover:border-primary/40 transition-colors text-left"
                >
                  <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-primary/10 text-primary mb-2">{label}</span>
                  <p className="text-sm text-foreground">"{suggestion}"</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto space-y-6">
          {messages
            .filter((message) => !message.pending)
            .map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3xl rounded-lg shadow-md ${
                  message.role === 'user'
                    ? 'bg-linear-to-r from-primary to-orange-400 text-white'
                    : 'bg-card border border-primary/20'
                }`}
              >
                <div className="px-4 py-3">
                  {message.role === 'assistant' ? (
                    <ChatMarkdown
                      content={(() => {
                        let text = message.content;
                        if (message.sql) {
                          text = text.replace(/```sql[\s\S]*?```/g, '').replace(/SQL QUERY:\s*/g, '');
                        }
                        if (message.chart_spec) {
                          text = text.replace(/\[MAP\]/g, '').replace(/\[CHART\]/g, '').replace(/\[GRAPH\]/g, '');
                          // Strip raw JSON chart specs the agent may dump in text
                          text = text.replace(/```json[\s\S]*?```/g, '');
                          text = text.replace(/\{[\s\S]*?"chart_type"[\s\S]*?"data"[\s\S]*?\}\s*$/g, '');
                        }
                        // Strip raw HTML tags (security: agent may hallucinate HTML)
                        text = text.replace(/<[^>]*>/g, '');
                        return text.trim();
                      })()}
                    />
                  ) : (
                    <>
                      {message.content.startsWith('\u{1F4CE}') && (
                        <div className="mb-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-white/90 text-xs font-medium">
                          <Paperclip className="h-3 w-3" />
                          {message.content.split('\n')[0].replace('\u{1F4CE} ', '')}
                        </div>
                      )}
                      <p className="whitespace-pre-wrap text-white">
                        {message.content.startsWith('\u{1F4CE}')
                          ? message.content.split('\n').slice(1).join('\n')
                          : message.content}
                      </p>
                    </>
                  )}

                  {/* Custom HTML Visualization (iframe) */}
                  {message.visualization && message.role === 'assistant' && (
                    <VisualizationEmbed html={message.visualization.html} title={message.visualization.title} />
                  )}

                  {/* Chart/Map Visualization (Recharts/Leaflet) */}
                  {message.chart_spec && !message.visualization && message.role === 'assistant' && (
                    <ChartRenderer spec={message.chart_spec} />
                  )}

                  {/* SQL Toggle */}
                  {message.sql && message.role === 'assistant' && (
                    <SqlToggle sql={message.sql} />
                  )}

                  {/* Execution Time */}
                  {message.execution_time_ms !== undefined && message.role === 'assistant' && !message.error && (
                    <div className="mt-3 text-xs text-muted-foreground/40 italic">
                      Completed in {(message.execution_time_ms / 1000).toFixed(1)}s
                    </div>
                  )}

                  {/* Error */}
                  {message.error && (
                    <div className="mt-3 text-xs text-destructive">
                      Error: {message.error}
                    </div>
                  )}
                </div>

                {/* Feedback & Evaluation Bar -- always visible on assistant messages */}
                {message.role === 'assistant' && !message.error && (
                  <div className="border-t border-primary/10">
                    <div className="px-4 py-2 flex items-center gap-2">
                      {/* Evaluation score badge (if available) */}
                      {message.evaluation && (
                        <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${getScoreColor(message.evaluation.score)}`}>
                          {message.evaluation.score.toFixed(1)}/5.0
                        </span>
                      )}

                      {/* Expand/collapse evaluation details */}
                      {message.evaluation && (
                        <button
                          onClick={() => toggleEvalExpand(message.id)}
                          className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                          aria-label={expandedEval[message.id] ? 'Collapse evaluation' : 'Expand evaluation'}
                          aria-expanded={!!expandedEval[message.id]}
                        >
                          <Zap className="h-3 w-3" />
                          Evaluation
                          {expandedEval[message.id]
                            ? <ChevronUp className="h-3 w-3" />
                            : <ChevronDown className="h-3 w-3" />
                          }
                        </button>
                      )}
                    </div>

                    {/* Expanded evaluation details */}
                    {message.evaluation && expandedEval[message.id] && (
                      <div className="px-4 pb-3 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground font-medium">Score:</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${getScoreColor(message.evaluation.score)}`}>
                            {message.evaluation.score.toFixed(1)}/5.0 - {getScoreCategory(message.evaluation.score)}
                          </span>
                          {message.evaluation.confidence && (
                            <span className="text-xs text-muted-foreground">
                              Confidence: {message.evaluation.confidence}
                            </span>
                          )}
                        </div>

                        {message.evaluation.critique && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-amber-400">Critique</div>
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap pl-3 border-l-2 border-amber-500/30">
                              {message.evaluation.critique}
                            </div>
                          </div>
                        )}

                        {message.evaluation.reasoning && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-orange-400">Reasoning</div>
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap pl-3 border-l-2 border-orange-500/30">
                              {message.evaluation.reasoning}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Progress Updates */}
          {isLoading && (() => {
            const pendingMsg = messages.find(m => m.pending);
            if (pendingMsg?.content && pendingMsg.content.length > 0) {
              return (
                <div className="flex justify-start">
                  <div className="max-w-3xl rounded-lg bg-card/80 border border-primary/20 px-4 py-3">
                    <p className="text-foreground/90 whitespace-pre-wrap">{pendingMsg.content}</p>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Thinking Indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-xl bg-linear-to-r from-primary/5 to-orange-500/5 backdrop-blur-xs border border-primary/10 px-5 py-3 flex items-center gap-4 shadow-lg">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-linear-to-r from-primary to-orange-400 rounded-full blur-md opacity-30 animate-pulse" />
                  <Loader2 className="w-5 h-5 animate-spin text-primary relative" style={{ animationDuration: '1.5s' }} />
                </div>
                <span className="text-sm font-medium bg-linear-to-r from-primary/80 to-orange-400/80 bg-clip-text text-transparent whitespace-nowrap">
                  {activityStatus}
                </span>
                <span className="text-xs text-muted-foreground/50 tabular-nums shrink-0 min-w-[40px] text-right">{elapsedTime}s</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
}
